import { Button } from "@repo/ui";

export function Home() {
  return (
    <div>
      {/* Header */}
      <header className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 md:px-10 py-4 md:py-5 text-white/90 backdrop-blur-md bg-black/20 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div
            className="h-7 w-7 rounded-md"
            style={{ background: "linear-gradient(135deg,#8A5A44,#A06E57)" }}
          />
          <span className="text-lg font-medium tracking-tight">Mano Studio</span>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a href="#work" className="hover:opacity-80">
            Work
          </a>
          <a href="#about" className="hover:opacity-80">
            About
          </a>
          <a href="#process" className="hover:opacity-80">
            Process
          </a>
          <a href="#contact" className="hover:opacity-80">
            Contact
          </a>
        </nav>
      </header>

      {/* Hero - Dark image background with light text on the left */}
      <section
        className="relative min-h-[70vh] md:min-h-[78vh] flex items-center"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25), rgba(0,0,0,0.1)), url(https://storage.stardeck.ai/images/590c3160-8e82-4f21-9d55-7971ea344581.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="px-6 md:px-10 w-full">
          <div className="max-w-2xl">
            <h1 className="display text-5xl md:text-6xl leading-[1.05] mb-6 text-white">
              Minimal ceramic pieces with a human touch.
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-prose mb-8">
              Mano Studio crafts small-batch, hand-built objects for the home—quiet forms, warm
              textures, designed to be lived with.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-white text-black hover:bg-white/90">
                <a href="#work">View Work</a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-white/20 border-white/60 text-white hover:bg-white/30 backdrop-blur-sm"
              >
                <a href="#contact">Inquire</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Work Grid */}
      <section id="work" className="px-6 md:px-10 py-12 md:py-20">
        <h2 className="display text-3xl md:text-4xl mb-8">Selected Work</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              src: "https://images.pexels.com/photos/19193216/pexels-photo-19193216.jpeg",
              title: "Vessel No. 3",
            },
            {
              src: "https://images.pexels.com/photos/6757759/pexels-photo-6757759.jpeg",
              title: "Sage Cylinder",
            },
            {
              src: "https://images.pexels.com/photos/7302794/pexels-photo-7302794.jpeg",
              title: "Table Objects",
            },
            {
              src: "https://images.pexels.com/photos/31493649/pexels-photo-31493649.jpeg",
              title: "Shelved Forms",
            },
            {
              src: "https://images.pexels.com/photos/14563975/pexels-photo-14563975.jpeg",
              title: "Marbled Vase",
            },
            {
              src: "https://images.pexels.com/photos/15440783/pexels-photo-15440783.jpeg",
              title: "Stoneware Trio",
            },
          ].map((item) => (
            <figure key={item.title} className="group">
              <img
                src={item.src}
                alt={item.title}
                className="w-full aspect-[4/3] object-cover rounded-lg"
              />
              <figcaption className="mt-2 text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {item.title}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* About */}
      <section id="about" className="px-6 md:px-10 py-12 md:py-20 bg-card/60">
        <div className="max-w-3xl">
          <h2 className="display text-3xl md:text-4xl mb-4">About</h2>
          <p className="text-lg text-muted-foreground">
            Founded on slow craft and material honesty, Mano Studio is the practice of making with
            hands. We explore form, proportion, and glaze to create enduring everyday pieces.
          </p>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="px-6 md:px-10 py-12 md:py-20">
        <h2 className="display text-3xl md:text-4xl mb-8">Process</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: "Hand-built",
              body: "Each piece is formed individually, embracing subtle variation.",
            },
            {
              title: "Small-batch",
              body: "Limited runs ensure attention to detail and material quality.",
            },
            {
              title: "Fired + finished",
              body: "Fired twice, then finished with food-safe glazes and care.",
            },
          ].map((s) => (
            <div key={s.title} className="space-y-2">
              <h3 className="font-semibold tracking-tight">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="px-6 md:px-10 py-14 md:py-24">
        <div className="rounded-xl border p-8 md:p-12 bg-white/70">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="display text-3xl md:text-4xl mb-3">Commissions & stockists</h2>
              <p className="text-muted-foreground mb-6">
                For bespoke work, wholesale, or collaborations, please reach out.
              </p>
              <div className="flex gap-3">
                <Button asChild>
                  <a href="mailto:hello@manostudio.design">Email us</a>
                </Button>
                <Button asChild variant="outline">
                  <a href="#work">View portfolio</a>
                </Button>
              </div>
            </div>
            <img
              src="https://images.pexels.com/photos/6476112/pexels-photo-6476112.jpeg"
              alt="Ceramic vases flat lay"
              className="w-full h-64 md:h-80 object-cover rounded-lg"
            />
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-10 py-10 text-sm text-muted-foreground">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <span>© {new Date().getFullYear()} Mano Studio</span>
          <span>Handmade in small batches</span>
        </div>
      </footer>
    </div>
  );
}
